import { useEffect, useRef, useState } from 'react';

const cities = [
  { name: 'Berlin', deliveryTime: '60 min', active: true },
  { name: 'Munich', deliveryTime: '60 min', active: true },
  { name: 'Hamburg', deliveryTime: '60 min', active: true },
  { name: 'Frankfurt', deliveryTime: '60 min', active: true },
  { name: 'Cologne', deliveryTime: '60 min', active: true },
  { name: 'Stuttgart', deliveryTime: '60 min', active: true },
  { name: 'Düsseldorf', deliveryTime: '60 min', active: true },
  { name: 'Dortmund', deliveryTime: '60 min', active: true },
  { name: 'Essen', deliveryTime: '60 min', active: true },
  { name: 'Leipzig', deliveryTime: '60 min', active: true },
  { name: 'Bremen', deliveryTime: '60 min', active: true },
  { name: 'Dresden', deliveryTime: '60 min', active: true },
];

export default function DeliveryMap() {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCity, setHoveredCity] = useState<string | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-primary-500 text-sm font-semibold tracking-wider uppercase">
            Delivery Coverage
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
            We Deliver Across Germany
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Fast, reliable delivery to major cities. Check if we deliver to your area.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Map Illustration */}
          <div
            className={`relative transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            <div className="relative bg-gradient-to-br from-primary-50 to-pink-50 rounded-3xl p-12 aspect-square flex items-center justify-center">
              {/* Simplified Germany Map Illustration */}
              <div className="relative w-full h-full">
                <img
                  src="https://readdy.ai/api/search-image?query=simplified%20illustrated%20map%20of%20Germany%20with%20major%20cities%20marked%20by%20green%20location%20pins%2C%20clean%20minimalist%20design%2C%20flat%20illustration%20style%2C%20light%20background%2C%20modern%20vector%20art%2C%20delivery%20coverage%20map%20visualization&width=600&height=600&seq=germany-map-001&orientation=squarish"
                  alt="Germany delivery map"
                  className="w-full h-full object-contain"
                />
                
                {/* Animated Pulse Effect */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-32 h-32 bg-primary-500/20 rounded-full animate-ping"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Cities List */}
          <div
            className={`transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="bg-white border border-gray-200 rounded-3xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Available Cities
              </h3>
              
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {cities.map((city, index) => (
                  <div
                    key={index}
                    onMouseEnter={() => setHoveredCity(city.name)}
                    onMouseLeave={() => setHoveredCity(null)}
                    className={`flex items-center justify-between p-4 rounded-xl transition-all cursor-pointer ${
                      hoveredCity === city.name
                        ? 'bg-primary-50 border-primary-200'
                        : 'bg-gray-50 border-transparent'
                    } border`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <i className="ri-map-pin-fill text-primary-500"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{city.name}</div>
                        <div className="text-sm text-gray-500">Delivery: {city.deliveryTime}</div>
                      </div>
                    </div>
                    <i className="ri-checkbox-circle-fill text-primary-500 text-xl"></i>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="mt-8 p-6 bg-gradient-to-r from-primary-50 to-pink-50 rounded-2xl">
                <h4 className="font-bold text-gray-900 mb-2">Don't see your city?</h4>
                <p className="text-sm text-gray-600 mb-4">
                  We're expanding rapidly. Enter your postcode to get notified when we arrive.
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Enter postcode"
                    className="flex-1 px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                  <button className="px-6 py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    Notify Me
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}